import { useRouter } from 'next/router';
import { useEffect, useRef } from 'react';

import Constant from '@/constants/const';
import useAccount from '@/store/account';
import axios from '@/utils/axios';
// import { refreshReferral } from '@/utils/client-middwares';
import { getLoginState } from '@/utils/common/user';
import { deleteCookie, getCookie, getCookies, setCookie } from '@/utils/cookie';
import { buildUrlWithOrigin } from '@/utils/url';

/**
 * 客户端无token时调用需要先等待全局 preorder 接口返回才可以, 涉及的组件一律需要使用这个 hooks 包裹
 * 也可以任何情况都使用, 但是只能执行一次
 * @returns
 */
export default function usePageMountEffect(
  onUserMountCallback: (data: { isLogin: boolean }) => void,
  deps?: Array<any>
) {
  const { userPreOrderLogged } = useAccount();
  useEffect(() => {
    //需要等待pre order返回之后再回调, 保证用户相关的 cookie 已经写入, 之后的业务获取没有问题
    const isLogin = getCookie(Constant.IS_LOGIN) === '1';
    onUserMountCallback?.({
      isLogin
    });
  }, [userPreOrderLogged, ...(deps || [])]);
}

/**
 * 扩展 useEffect, 为了拦截普通 useEffect 先触发的变动后导致 user 并没有确认, 需要等 userPreOrderLogged 为 true 时才可以执行
 * ex: 目前 pdp 页面的不走服务端中间件, 需要等待客户端完成之后才可以
 */
export const useUserEffect = (callback: () => void, deps?: Array<any>) => {
  const { userPreOrderLogged } = useAccount();
  const userLoggedRef = useRef(false);
  useEffect(() => {
    if (userPreOrderLogged) {
      userLoggedRef.current = true;
    }
  }, [userPreOrderLogged]);
  useEffect(() => {
    if (userLoggedRef.current) {
      callback();
    }
  }, deps);
};

export const doMiddwareLogin = (): Promise<boolean> => {
  const setDataToCookie = (key, val, maxAge?) => {
    setCookie(key, val, null, {
      maxAge: maxAge || Constant.AUTH_TOKEN_EXPIRED_TIME
    });
  };
  const reportMiddwareLoginError = (fnName: string, params: Record<string, any>) => {
    if (window.raptor) {
      window.raptor.reportLogs({
        fn: fnName,
        params: params
      });
    }
  };
  return axios
    .get(
      `/ec/bff/common/base/init?curPath=${encodeURIComponent(window.location.href)}`,
      {
        auth_token_channel: process.env.CHANNEL || ''
      },
      {
        headers: {
          'FTU-COOKIE': getCookie(Constant.FTU_COOKIE) || '',
          'referrer-id': getCookie(Constant.REFERRER_ID) || '',
          'referral-id': getCookie(Constant.REFERRAL_ID) || '',
          'referral-id-af': getCookie(Constant.REFERRAL_ID_AF) || ''
        }
      }
    )
    .then(resData => {
      if (!resData?.result || !resData?.object) {
        reportMiddwareLoginError('middware_api_error:', resData);
        return false;
      }
      const {
        lang,
        session,
        porderInfo,
        authToken,
        isLogin,
        ftuCookie,
        userId,
        isAffiliate,
        referralId,
        referrerId,
        referralIdAf,
        affiliateReferralId,
        weeeStore,
        store
      } = resData.object;
      if (!authToken || !porderInfo.zipcode || !porderInfo.delivery_pickup_date) {
        reportMiddwareLoginError('middware_data_empty_error:', {
          authToken,
          porderInfo
        });
        return false;
      }
      if (store) {
        setDataToCookie(Constant.STORE_INFO, store);
      }
      if (weeeStore) {
        setDataToCookie(Constant.WEEE_STORE, weeeStore);
      } else {
        deleteCookie(Constant.WEEE_STORE, weeeStore);
      }
      if (isLogin) {
        setDataToCookie(Constant.USER_ID, userId);
        setDataToCookie(Constant.AFFILIATE_REFERRAL_ID, affiliateReferralId);
      } else {
        deleteCookie(Constant.AFFILIATE_REFERRAL_ID);
        deleteCookie(Constant.USER_ID);
      }
      if (ftuCookie) {
        setDataToCookie(Constant.FTU_COOKIE, ftuCookie, Constant.FTU_COOKIE_EXPIRED_TIME);
      }
      if (referralIdAf) {
        setDataToCookie(Constant.REFERRAL_ID_AF, referralIdAf, Constant.REFERRAL_ID_EXPIRED_TIME);
      } else {
        deleteCookie(Constant.REFERRAL_ID_AF);
      }
      const deviceId = getCookie(Constant.B_COOKIE);
      if (!deviceId) {
        setDataToCookie(Constant.B_COOKIE, session, Constant.B_COOKIE_EXPIRED_TIME);
      }
      setDataToCookie(Constant.REFERRER_ID, referrerId, Constant.REFERRAL_ID_EXPIRED_TIME);
      setDataToCookie(Constant.REFERRAL_ID, referralId, Constant.REFERRAL_ID_EXPIRED_TIME);

      // setDataToCookie(Constant.IS_AFFILIATE, isAffiliate);
      setDataToCookie(Constant.COOKIE_LANGUAGE, lang);
      setDataToCookie(Constant.WEEE_SESSION_TOKEN, session);
      setDataToCookie(Constant.AUTH_TOKEN, authToken);
      setDataToCookie(Constant.ZIP_CODE, porderInfo.zipcode);
      setDataToCookie(Constant.ORDER_TOKEN, porderInfo.token);
      setDataToCookie(Constant.DEAL_ID, porderInfo.deal_id);
      setDataToCookie(Constant.PRE_ORDER_ID, porderInfo.id);
      setDataToCookie(Constant.ZIP_CITY, porderInfo.city || porderInfo.addr_city || '');
      setDataToCookie(Constant.DELIVERY_DATE, porderInfo.delivery_pickup_date || '');
      setDataToCookie(Constant.SALES_ORG_ID, porderInfo.sales_org_id || '');
      setDataToCookie(Constant.PANTRY_FREE_FEE, porderInfo.pantry_free_fee);
      setDataToCookie(Constant.PANTRY_FEE, porderInfo.pantry_shipping_fee);
      setDataToCookie(Constant.IS_LOGIN, isLogin ? '1' : '0');
      setDataToCookie(Constant.IS_SUPPORT_GLOBAL, porderInfo.is_show_tab_global_plus || '0');
      setDataToCookie(Constant.IS_SUPPORT_CHANGE_DATE, porderInfo.is_support_change_date ? '1' : '0');
      setDataToCookie(
        Constant.SELECT_ADDRESS,
        `${porderInfo.address || ''}|${porderInfo.addr_firstname || ''}|${porderInfo.addr_lastname || ''}|${
          porderInfo.email || ''
        }|${porderInfo.phone || ''}|${porderInfo.comment || ''}`
      );
      return true;
    })
    .catch((error: any) => {
      reportMiddwareLoginError('middware_catch_error:', error);
      return false;
    });
};

export async function useCheckUserPreOrder() {
  const { syncUserPreLogin, userPreOrderLogged } = useAccount();
  const router = useRouter();

  useEffect(() => {
    const postPreLoginStatus = () => {
      syncUserPreLogin(true);
      window.dispatchEvent(new Event('userPreOrderLogged'));
    };
    const initBiz = async () => {
      let lang = getCookie(Constant.LANGUAGE);
      if (lang === Constant.LANGUAGE_CHINESE_T_ALIAS) {
        lang = Constant.LANGUAGE_CHINESE_T;
      }
      // 兼容: 如果以后开启了cdn缓存, 跳过中间件无法做重定向, 这里需要修正语言以确保其他逻辑获取语言无误
      if (lang && router.locale !== lang) {
        const updateUrl = location.pathname.replace(Constant.LANGUAGE_REGEXP, `/${lang}`);
        location.replace(`${buildUrlWithOrigin(updateUrl)}${location.search}`);
        return;
      }

      if (!userPreOrderLogged) {
        let result = await doMiddwareLogin();
        if (result === false) {
          window.location.href = buildUrlWithOrigin(`/${lang}/404`);
          return;
        }
        postPreLoginStatus();
      }

      //校验用户状态
      // const userAuthed = await checkAllUserAuthCookies();
      //处理所有并行业务
      // const allCheckTasks = await Promise.all([checkStoreIfNeeded(), checkAffliate()]);
      //不需要重新执行 pre-order 流程
      // if (userAuthed) {
      //   if (allCheckTasks.every(checked => checked)) {
      //     syncUserPreLogin(true);
      //   }
      // } else {
      //   await doMiddwareLogin();
      //   syncUserPreLogin(true);
      // }
    };
    //目前实验的是 pdp 页面, 其他页面暂不需要走流程
    if (location.pathname.includes('/product/')) {
      initBiz().then(() => {
        // 如果 url 上有 w_source 字段, 需要使用 replace 剔除
        if (location.search.includes('w_source')) {
          const searchParams = new URLSearchParams(location.search);
          searchParams.delete('w_source');
          location.replace(`${location.pathname}${searchParams.toString() ? '?' + searchParams.toString() : ''}`);
          return;
        }
      });
    } else {
      postPreLoginStatus();
    }
  }, []);
}

// 检查: 如果返利联盟用户未上报用户数据, 需要在客户端再次执行一次
export async function checkAffliate() {
  // 登录, cookie记录了REFERRAL_ID_AF || 打开带referral_id的url 上报接口
  // (PEN-143)
  const refererId = new URLSearchParams(location.search);
  const referralAfCookie = getCookie(Constant.REFERRAL_ID_AF) || '';
  const referralAfCookieArray = referralAfCookie.split(':') || [];
  const referralAfId = referralAfCookieArray?.[0] || refererId;
  const userId = getCookie(Constant.USER_ID);
  const isLogin = getLoginState();
  if (isLogin && !!referralAfId && userId !== referralAfId) {
    const params = {
      referral_id: referralAfId,
      event_time_diff: !!referralAfCookieArray?.[1]
        ? Math.floor(Date.now() / 1000) - Number(referralAfCookieArray?.[1])
        : 0,
      page_url: referralAfCookieArray?.[2] || location.pathname
    };
    // const referralInfo = await refreshReferral({}, params);
    // if (referralInfo && referralInfo.result) {
    //   deleteCookie(Constant.REFERRAL_ID_AF);
    // }
  }

  return Promise.resolve(true);
}
